/**
 * Thin wrapper around window.dataLayer for Google Tag Manager (GTM-NR64TX8T).
 *
 * The site loads ONLY Google Tag Manager. GA4 (G-NC5CQJQBKF) and every Google Ads
 * conversion must be configured as tags inside GTM — see /app/memory/GTM_SETUP.md.
 * This file only pushes dataLayer events for GTM triggers:
 *   - confirmed_lead       — quote/contact/viewing saved by the backend (Google Ads conversion)
 *   - generate_lead        — GA4 twin of confirmed_lead (same payload; do NOT use for Ads)
 *   - price_estimate_view  — estimator price revealed (engagement, not a lead)
 *   - quote_page_view      — /get-a-quote landing page view
 *   - phone_click / whatsapp_click / email_click
 *
 * All events are safe to call when dataLayer is missing (no-op during SSR / ad-blocked sessions).
 */

// Google Ads conversions are configured inside GTM (AW-18540813437, trigger: confirmed_lead).

// Calibrated lead values (£) for value-based bidding in Google Ads.
// HomeGo offers only two core services — House Removals and Office Relocation.
// Other slugs (packing add-ons, legacy man-and-van entries from old chat
// transcripts, etc.) fall back to the house-removals tier since they almost
// always convert into a full house-move booking.
//
// Formula: avg_job_revenue × lead→booking_conversion_rate
//   House Removals:    £600 × 25%  = £150 / lead
//   Office Relocation: £1,000 × 20% = £200 / lead
const LEAD_VALUE_BY_SERVICE = {
  'house-removals': 150,
  'office-relocation': 200,
};
const DEFAULT_LEAD_VALUE = 150;

const resolveLeadValue = (service, packageName, explicitValue) => {
  if (typeof explicitValue === 'number' && explicitValue > 0) return explicitValue;
  if (service && LEAD_VALUE_BY_SERVICE[service]) return LEAD_VALUE_BY_SERVICE[service];
  // Try to match by package name (e.g. "House Removals" → "house-removals")
  if (packageName) {
    const slug = String(packageName).toLowerCase().replace(/\s+/g, '-');
    if (LEAD_VALUE_BY_SERVICE[slug]) return LEAD_VALUE_BY_SERVICE[slug];
  }
  return DEFAULT_LEAD_VALUE;
};

// Exported helper so non-analytics modules (e.g. QuoteForm building the
// /thank-you redirect URL) can compute the same calibrated value the
// conversion event will use. Returns { value, currency } so callers can
// pass it cleanly into URL params.
export function getLeadValueForConversion(service, packageName) {
  return { value: resolveLeadValue(service, packageName), currency: 'GBP' };
}

const safePush = (payload) => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  try {
    window.dataLayer.push(payload);
  } catch (_) {
    // swallow — analytics failure must never break the UX
  }
};

// ---------- Confirmed leads (the ONLY Google Ads lead conversion) ----------
// Contract (no names, emails, phones, addresses, postcodes or free text — ever):
//   { event: 'confirmed_lead', transaction_id, lead_type, form_source, value, currency: 'GBP' }
// plus a GA4-only twin { event: 'generate_lead', ...same fields } pushed once with it.
// Fired only after the backend confirms the save and returns an opaque ID; deduped per ID
// across refreshes / back-forward / new tabs (localStorage, with in-memory fallback).
const LEAD_TYPES = ['quote', 'contact', 'viewing'];
const FIRED_KEY = 'homego-confirmed-leads';
const firedMemory = new Set();
const readFired = () => {
  try { return JSON.parse(window.localStorage.getItem(FIRED_KEY) || '[]'); } catch (_) { return []; }
};
const alreadyFired = (id) => firedMemory.has(id) || readFired().includes(id);
const rememberFired = (id) => {
  firedMemory.add(id);
  try { window.localStorage.setItem(FIRED_KEY, JSON.stringify([...readFired(), id].slice(-50))); } catch (_) { /* private mode */ }
};
const SAFE_ID = /^[A-Za-z0-9_-]{8,64}$/;
const SAFE_TOKEN = /^[a-z0-9-]{1,40}$/;

// Quote-only home-size band for GTM (fixed tokens, never free text).
const PROPERTY_SIZES = ['studio', '1-bed', '2-bed', '3-bed', '4-plus-bed', 'office', 'single-items'];
const SIZE_ALIASES = {
  '1-bed-flat': '1-bed', '2-bed-house': '2-bed', '3-bed-house': '3-bed', '4-plus-bed-house': '4-plus-bed',
  Studio: 'studio', '1 bedroom': '1-bed', '2 bedrooms': '2-bed', '3 bedrooms': '3-bed', '4+ bedrooms': '4-plus-bed',
};
const toPropertySize = (raw) => {
  const v = SIZE_ALIASES[raw] || raw;
  return PROPERTY_SIZES.includes(v) ? v : 'unknown';
};

export function trackConfirmedLead({ leadId, leadType, formSource, service, packageName, propertySize } = {}) {
  if (typeof window === 'undefined') return false;
  const id = String(leadId || '');
  if (!SAFE_ID.test(id) || !LEAD_TYPES.includes(leadType) || alreadyFired(id)) return false;
  rememberFired(id);
  const source = SAFE_TOKEN.test(String(formSource || '')) ? formSource : 'unknown';
  const payload = {
    transaction_id: id,
    lead_type: leadType,
    form_source: source,
    value: leadType === 'quote' ? resolveLeadValue(service, packageName) : DEFAULT_LEAD_VALUE,
    currency: 'GBP',
    ...(leadType === 'quote' ? { property_size: toPropertySize(propertySize) } : {}),
  };
  safePush({ event: 'confirmed_lead', ...payload });
  safePush({ event: 'generate_lead', ...payload });
  try {
    if (typeof window.oaiq === 'function') window.oaiq('measure', 'lead_created', { type: 'customer_action' });
  } catch (_) { /* analytics must never break the form */ }
  try {
    if (typeof window.fbq === 'function') window.fbq('track', 'Lead', { value: payload.value, currency: 'GBP', content_category: leadType }, { eventID: id });
  } catch (_) { /* analytics must never break the form */ }
  return true;
}

/** Price estimator reveal — engagement only, never a lead. */
export function trackPriceEstimate({ propertySize, value } = {}) {
  safePush({
    event: 'price_estimate_view',
    estimate_property_size: SAFE_TOKEN.test(String(propertySize || '')) ? propertySize : null,
    value: typeof value === 'number' && value > 0 ? Math.round(value) : 0,
    currency: 'GBP',
  });
}

/**
 * Fire the Google Ads "Submit lead form (Page load)" conversion the moment
 * someone lands on the /get-a-quote landing page. Top-of-funnel signal for
 * Smart Bidding — a page view, not a lead.
 *
 * Idempotent within a session — guards against React Strict-Mode double mounts.
 */
let quotePageViewFired = false;
export function trackQuotePageView({ value = 0, currency = 'GBP' } = {}) {
  if (typeof window === 'undefined' || quotePageViewFired) return;
  quotePageViewFired = true;
  safePush({
    event: 'quote_page_view',
    page_path: '/get-a-quote',
    currency,
    value,
  });
}

/**
 * Fire GA4 `phone_click` event AND every configured Google Ads
 * "Phone Click" conversion. Attach via onClick to every <a href="tel:…">.
 *
 * 500ms dedup window prevents double-fire when both an explicit onClick
 * handler AND the global document-level click delegate (App.js
 * `useGlobalPhoneClickTracking`) catch the same tap.
 */
let lastPhoneClickAt = 0;
export function trackPhoneClick({ phone, label = 'header', branch = null } = {}) {
  const now = Date.now();
  if (now - lastPhoneClickAt < 500) return;
  lastPhoneClickAt = now;
  safePush({
    event: 'phone_click',
    phone_number: phone || null,
    click_location: label,
    click_branch: branch,
  });
}

/** WhatsApp link click (wa.me / api.whatsapp.com). 500ms dedupe like phone_click. Never includes message text. */
let lastWhatsappClickAt = 0;
export function trackWhatsappClick({ label = 'global-delegate', linkHost = null } = {}) {
  const now = Date.now();
  if (now - lastWhatsappClickAt < 500) return;
  lastWhatsappClickAt = now;
  safePush({ event: 'whatsapp_click', click_location: label, link_host: linkHost });
}

export function trackEmailClick({ email, label = 'header', branch = null } = {}) {
  safePush({ event: 'email_click', email: email || null, click_location: label, click_branch: branch });
}
